import { Link, useLocation } from '@tanstack/react-router';
import { Button } from '@upcaret/ui';
import { Mail } from 'lucide-react';
import Header from '../../components/header';
import Footer from '../../components/footer';
import './not-found.css';
import {
  LEAN_ESCAPE_ROUTES,
  REPORT_EMAIL,
} from './site-pages';

type NotFoundPageProps = {
  /** When true, header/footer come from the marketing layout route. */
  embedded?: boolean;
};

function formatAttemptedPath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname;
}

function buildReportMailto(attemptedPath: string) {
  const subject = 'Broken link on upcaret.com';
  const body = [
    'Hi upCaret team,',
    '',
    'I hit a page that isn’t available:',
    attemptedPath,
    '',
    'Thanks for taking a look!',
  ].join('\n');

  return `mailto:${REPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function NotFoundPage({ embedded = false }: NotFoundPageProps) {
  const { pathname } = useLocation();
  const attemptedPath = formatAttemptedPath(pathname);
  const reportHref = buildReportMailto(attemptedPath);

  const content = (
    <div className="not-found-page not-found-enter mx-auto w-full min-w-0 max-w-3xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <p className="m-0 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
          <span aria-hidden="true">404</span>
          Page not found
        </p>
        <h1 className="mt-4 text-balance text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
          That link took a detour.
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-pretty text-base leading-relaxed text-slate-600 sm:text-lg">
          We couldn’t find that page. The link might be outdated, or the URL might
          have a typo. Either way, we’ll help you get where you meant to go.
        </p>
        <p className="mx-auto mt-4 max-w-2xl text-sm text-slate-500">
          You were headed to{' '}
          <code className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800 sm:text-sm">
            {attemptedPath}
          </code>
        </p>
      </div>

      <section className="mt-9 rounded-2xl border border-slate-200 bg-white p-5 shadow-xs sm:p-6">
        <h2 className="text-lg font-semibold text-slate-950">
          Try one of these pages
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          These links are live and can get you back on track quickly.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {LEAN_ESCAPE_ROUTES.map((route) => (
            <Button
              key={route.path}
              asChild
              variant={route.path === '/contact' ? 'default' : 'outline'}
              className="min-h-11 w-full"
            >
              <Link to={route.path}>{route.label}</Link>
            </Button>
          ))}
        </div>
      </section>

      <p className="mt-5 flex items-center justify-center gap-1 text-center text-sm leading-none text-slate-600">
        <span>Found a broken link?</span>
        <a
          href={reportHref}
          className="inline-flex items-center gap-1 leading-none font-medium text-primary underline-offset-4 hover:underline"
        >
          <Mail className="size-4" aria-hidden="true" />
          Report it
        </a>
      </p>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-background font-sans antialiased">
      <div className="flex min-h-screen min-w-0 flex-col">
        <Header />
        <main className="min-w-0 flex-1">{content}</main>
        <Footer />
      </div>
    </div>
  );
}

export default NotFoundPage;
